<template>
  <wrld-box :headerTitle="$t('reports.packages_weight_volume')">
    <template v-slot:boxContent>
      <h3 class="pl-4 pt-2">{{$t('reports.total_packages_per_day')}}</h3>
      <apexchart type=bar height=200 :options="chartOptionsPackages" :series="seriesPackages" />

      <h3 class="pl-4">{{$t('reports.total_weight_per_day')}}</h3>
      <apexchart type=bar height=200 :options="chartOptionsWeight" :series="seriesWeight" />

      <h3 class="pl-4">{{$t('reports.total_volume_per_day')}}</h3>
      <apexchart type=bar height=200 :options="chartOptionsVolume" :series="seriesVolume" />
    </template>
  </wrld-box>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { createWidgetMixin } from 'vue-instantsearch'
import { connectHits } from 'instantsearch.js/es/connectors/'
import VueApexCharts from 'vue-apexcharts'
import { algoliaService } from '@/_services'
import { generalHelpers } from '@/_helpers'

export default {
  name: 'MetricsPerDay',
  components: {
    'apexchart': VueApexCharts
  },
  mixins: [
    createWidgetMixin({ connector: connectHits })
  ],
  props: {
    mainDateFilter: String,
    indexName: String
  },
  data () {
    return {
      firstCall: true,
      seriesPackages: [],
      seriesWeight: [],
      seriesVolume: [],
      chartOptionsPackages: {
        chart: {
          id: 'packages',
          group: 'metrics'
        },
        colors: ['#A300D6'],
        dataLabels: {
          enabled: false
        },
        xaxis: {
          type: 'datetime'
        },
        yaxis: {
          labels: {
            minWidth: 20
          }
        },
        annotations: {
          xaxis: [{
            x: new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()).getTime(),
            borderColor: '#999',
            yAxisIndex: 0,
            label: {
              show: true,
              text: this.$t('reports.today'),
              style: {
                color: '#fff',
                background: '#000'
              }
            }
          }]
        }
      },
      chartOptionsWeight: {
        chart: {
          id: 'weight',
          group: 'metrics',
          toolbar: {
            show: false
          }
        },
        colors: ['#7D02EB'],
        dataLabels: {
          enabled: false
        },
        xaxis: {
          type: 'datetime'
        },
        yaxis: {
          labels: {
            minWidth: 20
          }
        },
        annotations: {
          xaxis: [{
            x: new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()).getTime(),
            borderColor: '#999',
            yAxisIndex: 0,
            label: {
              show: true,
              text: this.$t('reports.today'),
              style: {
                color: '#fff',
                background: '#000'
              }
            }
          }]
        }
      },
      chartOptionsVolume: {
        chart: {
          id: 'volume',
          group: 'metrics',
          toolbar: {
            show: false
          }
        },
        colors: ['#5653FE'],
        dataLabels: {
          enabled: false
        },
        xaxis: {
          type: 'datetime'
        },
        yaxis: {
          labels: {
            minWidth: 20
          }
        },
        annotations: {
          xaxis: [{
            x: new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()).getTime(),
            borderColor: '#999',
            yAxisIndex: 0,
            label: {
              show: true,
              text: this.$t('reports.today'),
              style: {
                color: '#fff',
                background: '#000'
              }
            }
          }]
        }
      }
    }
  },
  computed: {
    ...mapState(['generalApp']),
    needForUpdateData () {
      return { dateFilter: this.generalApp.algoliaDateFilters, allFilter: this.generalApp.allSelectedFilters }
    }
  },
  methods: {
    ...mapActions({
      startLoading: 'startLoading',
      stopLoading: 'stopLoading'
    }),
    async getData () {
      this.startLoading()

      let dateRange = {}
      for (let i = 0; i < Object.keys(this.generalApp.algoliaDateFilters).length; i++) {
        const dateField = this.generalApp.algoliaDateFilters[Object.keys(this.generalApp.algoliaDateFilters)[i]]
        const splitted = dateField.split(':')
        if (splitted[0] === this.mainDateFilter) {
          const timeStamps = splitted[1].split(' ')
          dateRange = { from: Number(timeStamps[0]), to: Number(timeStamps[2]) }
        }
      }
      const chunkSize = 4 // number of how many days will be included in 1 Algolia call (ideal if power of 2)
      const data = await algoliaService.getHitsDayByDay(this.indexName, this.generalApp.allSelectedFilters, this.mainDateFilter, dateRange, chunkSize)
      const dataLength = data.length
      let groupedByDate = {}

      for (let i = 0; i < dataLength; i++) {
        const element = data[i]
        const timeStamp = element.pickup.ts

        if (groupedByDate[timeStamp] === undefined) {
          groupedByDate[timeStamp] = {
            packages: 0,
            weight: 0,
            volume: 0
          }
        }
        const packages = groupedByDate[timeStamp].packages + element.packages
        const weight = groupedByDate[timeStamp].weight + element.weight
        const volume = groupedByDate[timeStamp].volume + element.volume

        groupedByDate[timeStamp] = {
          packages: packages,
          weight: weight,
          volume: volume
        }
      }

      let seriesPackages = []
      let seriesWeight = []
      let seriesVolume = []

      const groupedByDateLength = Object.keys(groupedByDate).length

      for (let i = 0; i < groupedByDateLength; i++) {
        const date = Object.keys(groupedByDate)[i] * 1000
        const values = groupedByDate[Object.keys(groupedByDate)[i]]

        seriesPackages.push([date, values.packages])
        seriesWeight.push([date, Math.round(values.weight * 100) / 100])
        seriesVolume.push([date, Math.round(values.volume * 100) / 100])
      }

      // TODO: add to i18n
      this.seriesPackages = [{ name: 'Packages', data: seriesPackages }]
      this.seriesWeight = [{ name: 'Weight', data: seriesWeight }]
      this.seriesVolume = [{ name: 'Volume', data: seriesVolume }]

      this.stopLoading()
    }
  },
  watch: {
    needForUpdateData: {
      deep: true,
      handler (nawVal, oldVal) {
        if (!generalHelpers.isEqual(nawVal, oldVal) || this.firstCall) {
          this.getData()
          this.firstCall = false
        }
      }
    }
  }
}
</script>
